/**
 * The client half of the token contract.
 *
 * The access token lives in this module's closure and nowhere else — not localStorage, not
 * sessionStorage, not a cookie the page can read — so a script injected into the page has no
 * store to steal it from, and a reload simply loses it. The refresh token is issued by the API in
 * an `HttpOnly; Secure; SameSite=Strict` cookie that JavaScript cannot see at all; the browser
 * attaches it to the refresh call and nothing here ever touches it. Losing the access token on
 * reload is therefore harmless: the client asks the cookie for a new one at start-up.
 */

type TokenListener = (token: string | null) => void
type ExpiryListener = () => void

let accessToken: string | null = null
let selectedBranchId: string | null = null

const tokenListeners = new Set<TokenListener>()
const expiryListeners = new Set<ExpiryListener>()

const BRANCH_STORAGE_KEY = 'microcredit.selected-branch'

export const session = {
  getAccessToken: () => accessToken,

  setAccessToken(token: string | null) {
    accessToken = token
    tokenListeners.forEach((listener) => listener(token))
  },

  clear() {
    session.setAccessToken(null)
  },

  onAccessTokenChange(listener: TokenListener) {
    tokenListeners.add(listener)
    return () => {
      tokenListeners.delete(listener)
    }
  },

  /**
   * Raised when the refresh token is gone or rejected and no retry can recover the session. The
   * shell listens and sends the user to the sign-in screen with their location preserved.
   */
  onExpired(listener: ExpiryListener) {
    expiryListeners.add(listener)
    return () => {
      expiryListeners.delete(listener)
    }
  },

  notifyExpired() {
    accessToken = null
    tokenListeners.forEach((listener) => listener(null))
    expiryListeners.forEach((listener) => listener())
  },

  /**
   * The branch the user is currently operating in. Head-office users may hold access to several;
   * the choice rides on every request as a header and the server validates it against the user's
   * branch access on each one — picking a branch here grants nothing.
   */
  getBranchId: () => selectedBranchId,

  setBranchId(branchId: string | null) {
    selectedBranchId = branchId
    try {
      if (branchId) {
        window.localStorage.setItem(BRANCH_STORAGE_KEY, branchId)
      } else {
        window.localStorage.removeItem(BRANCH_STORAGE_KEY)
      }
    } catch {
      // Private browsing and locked-down kiosk profiles can refuse storage. The selection still
      // holds for this tab; it simply will not survive a reload.
    }
  },

  restoreBranchId() {
    try {
      selectedBranchId = window.localStorage.getItem(BRANCH_STORAGE_KEY)
    } catch {
      selectedBranchId = null
    }

    return selectedBranchId
  },
}
